/*
 * Problem alerts (BAR's and SupCom's "unit idle" notices): what the
 * commander's state says isn't working and they can fix, in plain words
 * with what to do. Each case must hold for a while before it shows (a
 * brief shortage doesn't flash), and cases of a kind come as one alert
 * with a count, the camera cycling through them.
 */

import type { Alert } from "./alerts";
import { takersOf } from "./industry";

type Hex = [number, number];

interface ServerSite {
  at: Hex;
  kind: "mine" | "plant";
  name: string;
  why?: string;
  missing?: string[];
}

interface ServerBarracks {
  at: Hex;
  waiting?: boolean;
  why?: string;
  missing?: string[];
  foundry?: boolean;
}

interface ServerSiteBuilding {
  what: string;
  at: Hex;
  to?: Hex;
  recipe?: string;
}

/** How long a case must hold before it shows, by how brief it can be. */
const SOON = 8_000;
const SETTLED = 30_000;
const SHORTAGE = 60_000;

const dist = (a: Hex, b: Hex) => Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[0] - b[0] + a[1] - b[1]));
const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
/** "Iron ore mine" ×3: "3 iron ore mines" (works and barracks stay). */
function many(name: string, n: number): string {
  if (n === 1) return name;
  const plural = /(works|barracks|s)$/i.test(name) ? name : `${name}s`;
  return `${n} ${lower(plural)}`;
}

export class Problems {
  /** When each case was first seen ("kind|name|good@q,r"). */
  private since = new Map<string, number>();
  private kept: { industry: unknown[]; barracks: unknown[]; sites: unknown[]; engineers: unknown[] } = { industry: [], barracks: [], sites: [], engineers: [] };

  /** The alerts the state calls for now; `keys` names the build menu's
   *  and Stop's keys in words ("B", "S"). */
  compute(full: Record<string, unknown>, now: number, keys: { build: string; stop: string }): Alert[] {
    // A state without a part (not every state carries all of them) keeps
    // the last one's, so its cases don't start their wait again.
    const c = full.construction as { sites?: unknown; engineers?: unknown } | undefined;
    if (Array.isArray(full.industry)) this.kept.industry = full.industry;
    if (Array.isArray(full.barracks)) this.kept.barracks = full.barracks;
    if (Array.isArray(c?.sites)) this.kept.sites = c!.sites as unknown[];
    if (Array.isArray(c?.engineers)) this.kept.engineers = c!.engineers as unknown[];
    const state: Record<string, unknown> = { industry: this.kept.industry, barracks: this.kept.barracks, construction: { sites: this.kept.sites, engineers: this.kept.engineers } };
    const seen = new Set<string>();
    const groups = new Map<string, Alert>();
    const add = (group: string, at: Hex, wait: number, make: () => Omit<Alert, "id" | "at">) => {
      const key = `${group}@${at[0]},${at[1]}`;
      seen.add(key);
      const t = this.since.get(key) ?? now;
      this.since.set(key, t);
      if (now - t < wait) return;
      const a = groups.get(group);
      if (a) a.at.push(at);
      else groups.set(group, { id: group, ...make(), at: [at] });
    };

    const building = ((state.construction as { sites?: ServerSiteBuilding[] } | undefined)?.sites ?? []) as ServerSiteBuilding[];
    // Track being laid near it: the fix is under way.
    const trackNear = (at: Hex) => building.some((b) => b.what === "track" && (dist(b.at, at) <= 6 || (b.to && dist(b.to, at) <= 6)));
    // A plant going up that takes this good: the fix is under way.
    const takerBuilding = (good: string) =>
      building.some((b) => b.what === "plant" && !!b.recipe && takersOf(good).includes(b.recipe.toLowerCase()));

    // A mine digs every ore in its reach, a site each: one structure, so
    // one case, named by its first ore, and only when the whole mine is
    // stuck (a stray ore nothing takes, beside one that's used, is fine).
    const sites = (state.industry as ServerSite[] | undefined) ?? [];
    const mines = new Map<string, ServerSite[]>();
    for (const x of sites) if (x.kind === "mine") mines.set(`${x.at}`, [...(mines.get(`${x.at}`) ?? []), x]);
    for (const list of mines.values()) {
      const [first] = list;
      const at = first.at;
      if (list.some((x) => x.why === "no_track")) {
        if (!trackNear(at)) add(`no_track|${first.name}`, at, SETTLED, () => ({ icon: "unlinked", label: "", detail: "" }));
      } else if (list.every((x) => x.why === "unused" || x.why === "depleted")) {
        const unused = list.find((x) => x.why === "unused");
        const ore = (unused ?? first).name.replace(/ mine$/, "");
        if (!unused) add(`depleted|${first.name}`, at, SOON, () => ({ icon: ore, label: "", detail: "" }));
        else if (!takerBuilding(ore)) add(`unused|${unused.name}`, at, SETTLED, () => ({ icon: ore, label: "", detail: "" }));
      }
    }
    for (const x of sites) {
      if (!x.why || x.kind === "mine") continue;
      const good = x.missing?.[0];
      if (x.why === "no_track" && !trackNear(x.at)) add(`plant_no_track|${x.name}`, x.at, SETTLED, () => ({ icon: "unlinked", label: "", detail: "" }));
      else if (x.why === "cut_off" && good) add(`cut_off|${x.name}|${good}`, x.at, SETTLED, () => ({ icon: "unlinked", label: "", detail: "" }));
      else if (x.why === "inputs" && good) add(`inputs|${x.name}|${good}`, x.at, SHORTAGE, () => ({ icon: good, label: "", detail: "" }));
    }

    for (const b of (state.barracks as ServerBarracks[] | undefined) ?? []) {
      if (!b.waiting || !b.why) continue;
      const kind = b.foundry ? "Foundry" : "Barracks";
      if (b.why === "no_camp") add(`no_camp|${kind}`, b.at, SOON, () => ({ icon: "Barracks", label: "", detail: "" }));
      else if (b.why === "titan_at_foundry" || b.why === "foundry_titans_only") add(`${b.why}|${kind}`, b.at, SOON, () => ({ icon: "Barracks", label: "", detail: "" }));
      else if (b.why === "steel") add(`b_short|${kind}|Steel`, b.at, SHORTAGE, () => ({ icon: "Steel", label: "", detail: "" }));
      else if (b.why === "recruits") add(`b_short|${kind}|Recruits`, b.at, SHORTAGE, () => ({ icon: "Recruits", label: "", detail: "" }));
      else if (b.why === "goods" && b.missing?.[0]) add(`b_short|${kind}|${b.missing[0]}`, b.at, SHORTAGE, () => ({ icon: b.missing![0], label: "", detail: "" }));
    }

    for (const e of ((state.construction as { engineers?: { at: Hex; doing: string }[] } | undefined)?.engineers ?? [])) {
      if (e.doing === "idle") add("engineers_idle", e.at, SOON, () => ({ icon: "Engineers", label: "", detail: "" }));
      // Under orders and standing on one hex for a while: somewhere they
      // can't get to (arrived, the game lets them go back to building).
      if (e.doing === "orders") add("engineers_stuck", e.at, SETTLED, () => ({ icon: "Engineers", label: "", detail: "" }));
      // Going to a site and on one hex for a minute: they can't get there.
      if (e.doing === "going") add("engineers_blocked", e.at, SHORTAGE, () => ({ icon: "Engineers", label: "", detail: "" }));
    }

    for (const k of [...this.since.keys()]) if (!seen.has(k)) this.since.delete(k);

    // The words, now the counts are known.
    for (const a of groups.values()) {
      const [kind, name, good] = a.id.split("|");
      const n = a.at.length;
      const one = n === 1;
      const its = one ? "its" : "their";
      const g = good ? lower(good) : "";
      switch (kind) {
        case "no_track": {
          const ore = lower(name.replace(/ mine$/, ""));
          a.label = `${many(name, n)} ${one ? "has" : "have"} no track: ${its} ore can't leave`;
          a.detail = `Lay track from ${its} station to a plant that takes ${ore} (${takersOf(name.replace(/ mine$/, ""))}).`;
          break;
        }
        case "unused": {
          const ore = name.replace(/ mine$/, "");
          a.label = `${many(name, n)}: nothing on ${its} rail takes ${lower(ore)}`;
          a.detail = `Build ${takersOf(ore)} on that track.`;
          break;
        }
        case "depleted":
          a.label = `${many(name, n)} dug out`;
          a.detail = "The ore in reach is gone: build a mine somewhere new.";
          break;
        case "plant_no_track":
          a.label = `${many(name, n)} ${one ? "has" : "have"} no track: nothing can reach ${one ? "it" : "them"}`;
          a.detail = `Lay track from ${its} station to the mines and plants that supply ${one ? "it" : "them"}.`;
          break;
        case "cut_off":
          a.label = `${many(name, n)} cut off from ${g}`;
          a.detail = `Nothing on ${its} rail has or makes ${g}: join ${its} track to where ${g} comes from, or build what makes it on that track.`;
          break;
        case "inputs":
          a.label = `${many(name, n)} short of ${g}`;
          a.detail = `${one ? "Its" : "Their"} station is out of ${g}: make more, or lay more track to where it comes from.`;
          break;
        case "no_camp":
          a.label = `${many(name, n)} ${one ? "needs" : "need"} a training camp`;
          a.detail = "This design has training: build a training camp within 12 hexes, or pick another design.";
          break;
        case "titan_at_foundry":
          a.label = `${many(name, n)} can't make titans`;
          a.detail = "Only a foundry makes titans: pick another design, and build a foundry for titans.";
          break;
        case "foundry_titans_only":
          a.label = `${many(name, n)} ${one ? "makes" : "make"} only titans`;
          a.detail = "Set it to a titan design.";
          break;
        case "b_short":
          a.label = `${many(name, n)} short of ${g}`;
          a.detail =
            good === "Steel"
              ? `${one ? "Its" : "Their"} station has no steel for the next unit: it comes by rail from a steelworks.`
              : good === "Recruits"
                ? `No recruits at ${its} station: they come by rail from your home station.`
                : `${one ? "Its" : "Their"} station is out of ${g}: it comes by rail from the plants that make it.`;
          break;
        case "engineers_idle":
          a.label = one ? "Engineers idle" : `${n} engineers idle`;
          a.detail = `Nothing to build: choose a structure from the build menu (${keys.build}) and click the map.`;
          break;
        case "engineers_blocked":
          a.label = one ? "Engineers can't reach the site they're going to" : `${n} engineers can't reach the site they're going to`;
          a.detail = "Nothing gets built while they try: cancel that site (select it, Delete) or build it where they can walk to.";
          break;
        case "engineers_stuck":
          a.label = one ? "Engineers can't get where you sent them" : `${n} engineers can't get where you sent them`;
          a.detail = `They stand under your orders and build nothing else: select them and Stop (${keys.stop}), and they go back to building on their own.`;
          break;
      }
    }
    return [...groups.values()];
  }
}

/** The one watch, kept across a live world's hellos (each builds the game's
 *  controls anew; the waits mustn't start again with them). */
export const problems = new Problems();
